import { createSelector } from 'reselect';

/**
 * getMailDomains
 * Selector for the mailDomains state.
 */
export const getMailDomains = (state) => state?.settings?.mailDomains || {};

/**
 * getIsLoading
 * Selects the isLoading data from state.
 */
export const getIsLoading = (state) => getMailDomains(state).isLoading;

/**
 * getSendingDomains
 * Selects the sendingDomains data from state.
 */
export const getSendingDomains = (state) => getMailDomains(state).sendingDomains;

/**
 * getTrackingDomains
 * Selects the trackingDomains data from state.
 */
export const getTrackingDomains = (state) => getMailDomains(state).trackingDomains;

/**
 * getDefaultLinkShorteningDomain
 * Selects the default link shortening domain data from state.
 */
export const getDefaultLinkShorteningDomain = (state) =>
  getMailDomains(state).defaultSmsLinkShorteningDomain;

/**
 * getVerificationErrors
 * Selects the verificationErrors data from state.
 */
export const getVerificationErrors = (state) => getMailDomains(state).verificationErrors;

/**
 * getLinkShorteningTemplateIds
 * Selects the linkShorteningTemplateIds data from state.
 */
export const getLinkShorteningTemplateIds = (state) =>
  getMailDomains(state).linkShorteningTemplateIds || [];

/**
 * getAppSiteAssociations
 * Selects the appSiteAssociations data from state..
 */
export const getAppSiteAssociations = (state) => getMailDomains(state).appSiteAssociations;

/**
 * getAssetLinks
 * Selects the androidAssetsLinks data from state.
 */
export const getAssetLinks = (state) => getMailDomains(state).assetLinks;

/**
 * getDefaultTrackingDomain
 * Selects the default tracking Domain from state.
 */
export const selectDefaultTrackingDomain = createSelector([getTrackingDomains], (trackingDomains) =>
  trackingDomains.find((domain) => domain.isDefault === true),
);

/**
 * getTrackingDomains
 * Selects the trackingDomains data from state.
 */
export const getTrackingDomainName = (_, { trackingDomainName }) => trackingDomainName;

/**
 * getTrackingDomains
 * Selects the trackingDomains data from state.
 */
export const getOrganizationId = (_, { organizationId }) => organizationId;

/**
 * getAppDomainName
 * Selects the appDomain namne from props.
 */
export const getAppDomainName = (_, { appDomainName }) => appDomainName;

/**
 * getEspAccounts
 * Selects the espAccounts data from state.
 */
export const getEspAccounts = (state) => getMailDomains(state).espAccounts;

/**
 * selectSendingDomainsWithEspDetails
 * Selects sending domains and then returns them with the
 * associated espAccount data from state.
 */
export const selectSendingDomainsWithEspDetails = createSelector(
  [getSendingDomains, getEspAccounts],
  (sendingDomains, espAccounts) =>
    sendingDomains.map((sendingDomain) => {
      if (!sendingDomain.espAccountId) {
        return sendingDomain;
      }

      return {
        ...sendingDomain,
        espAccount: espAccounts.find(({ id }) => id === sendingDomain.espAccountId),
      };
    }),
);

/**
 * selectTrackingDomainFromDomainName
 * Selects trackkingDomain from the domain name
 */
export const selectTrackingDomainFromDomainName = createSelector(
  [getTrackingDomains, getTrackingDomainName],
  (trackingDomains, trackingDomainName) => {
    return trackingDomains.find(({ domain }) => domain === trackingDomainName) || {};
  },
);

/**
 * selectTrackingDomainHasActiveLegacyAppSiteAssociation
 * Selects whether an enabled legacy, or top-level domain exists for the specific tracking domain
 */
export const selectTrackingDomainHasActiveLegacyAppSiteAssociation = createSelector(
  [getAppSiteAssociations, getTrackingDomainName],
  (appSiteAssociations, trackingDomainName) => {
    const associationsByTrackingDomain = appSiteAssociations.filter(
      ({ domain }) => domain === trackingDomainName,
    );
    const legacyDomain = associationsByTrackingDomain.find((association) => !association.appDomain);
    return !!legacyDomain?.enabledForProject;
  },
);

/**
 * selectAppSiteAssociationsByTrackingDomain
 * Selects AppSiteAssociations by the provided tracking domain
 * as long as they have an app path
 */
export const selectAppSiteAssociationsByTrackingDomain = createSelector(
  [getAppSiteAssociations, getTrackingDomainName],
  (appSiteAssociations, trackingDomainName) =>
    appSiteAssociations.filter(({ domain }) => domain === trackingDomainName),
);

/**
 * selectEnabledDestinationDomainAppSiteAssociationsByTrackingDomain
 * Selects AppSiteAssociations by the provided tracking domain
 * as long as they have an app path
 */
export const selectEnabledDestinationDomainAppSiteAssociationsByTrackingDomain = createSelector(
  [selectAppSiteAssociationsByTrackingDomain],
  (associationsByTrackingDomain) =>
    associationsByTrackingDomain.filter(
      ({ appDomain, enabledForProject }) => !!appDomain && enabledForProject,
    ),
);

/**
 * selectAppSideAssociationForPath
 * Selects appleAppSiteAssociationFile for a specific path
 */
export const selectAppSideAssociationForPath = createSelector(
  [getAppSiteAssociations, getTrackingDomainName, getAppDomainName],
  (appSiteAssociations, trackingDomainName, appDomainName) => {
    const appSiteAssociation = appSiteAssociations.find(
      ({ domain, appDomain }) => domain === trackingDomainName && appDomain === appDomainName,
    );
    return appSiteAssociation?.appleAppSiteAssociationFile;
  },
);

/**
 * selectIfAssetLinksExist
 * Boolean value of whether android asset links exist in the org
 */
export const selectIfAssetLinksExist = createSelector(
  [getAssetLinks],
  (assetLinks) => assetLinks.length !== 0,
);

/**
 * selectTrackingDomainsWithDeepLinksAndLinkShorteningTemplateIds
 * Selects AppSiteAssociations by the provided tracking domain
 * as long as they have an app path
 */
export const selectTrackingDomainsWithDeepLinksAndLinkShorteningTemplateIds = createSelector(
  [
    getTrackingDomains,
    getAppSiteAssociations,
    selectIfAssetLinksExist,
    getLinkShorteningTemplateIds,
  ],
  (trackingDomains, appSiteAssociations, assetLinksExist, linkShorteningTemplateIds) => {
    return trackingDomains.map((trackingDomain) => {
      const deepLinks = appSiteAssociations.filter(
        ({ domain }) => trackingDomain.domain === domain,
      );

      const templateIdsWithEnabledLinkShortening =
        linkShorteningTemplateIds.find((ls) => ls.trackingDomains === trackingDomain.domain)
          ?.templateIds || [];

      return {
        ...trackingDomain,
        deepLinks,
        assetLinksExist,
        templateIdsWithEnabledLinkShortening,
      };
    });
  },
);

/**
 * selectAssetLinksByOrgIdAndDomain
 * Selects the androidAssetsLinks based on orgId and trackingDomanName.
 */
export const selectAssetLinksByOrgIdAndDomain = createSelector(
  [getAssetLinks, getTrackingDomainName, getOrganizationId],
  (assetLinks, trackingDomainName, organizationId) => {
    const assetLinksForTrackingDomain = assetLinks.find(
      (assetLink) =>
        assetLink.domain === trackingDomainName && assetLink.organizationId === organizationId,
    );
    return assetLinksForTrackingDomain?.androidAssetLinks;
  },
);

/**
 * selectProjectHasSPFRecords
 * Selects whether any sending domains inside the project have a populated SPF record
 */
export const selectProjectHasSPFRecords = createSelector([getSendingDomains], (sendingDomains) =>
  sendingDomains.some(({ spf }) => spf !== undefined),
);
